import Typography from "@mui/material/Typography";
import { Frequency, TaskStatus, TaskType } from "./constants";
import SwitchToggle from "../ui/SwitchToggle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlarmOffRounded, AlarmOnRounded, ChatRounded, UpdateRounded } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { formatCadence, notifyTaskScheduleError } from "./helpers";
import { formatDate } from "../../helpers";
import classNames from "classnames";
import { useDispatch } from "react-redux/es/hooks/useDispatch";
import { AppDispatch } from "../../redux/store";
import { setSelectedTask, setShowTaskModal, updateTask } from "../../redux/slices/taskSlice";
import { debounce } from "lodash";
import useEnhancedNavigate from "../Session/helpers";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import { useSelector } from "react-redux";
import { selectActiveTaskCount } from "./";
import { taskLimitByPlan } from "./constants";
import { showNotification } from "../../redux/slices/notificationSlice";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { updateTaskStatus } from "../Sidebar/hooks";

type TaskProps = {
  task: TaskType;
};

const Task = ({ task }: TaskProps) => {
  const [isActive, setIsActive] = useState(false);
  const { cadence, by_week_day, by_month_day, by_month, by_year, by_hour, by_minute, timezone } = task;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useEnhancedNavigate();

  const countOfActiveTasks = useSelector(selectActiveTaskCount);
  const formattedString = useMemo(
    () =>
      formatCadence(
        cadence,
        by_week_day,
        by_month_day,
        by_month,
        by_year,
        by_hour,
        by_minute,
        timezone,
      ),
    [cadence, by_week_day, by_month_day, by_month, by_year, by_hour, by_minute, timezone],
  );
  const plan = useSelector((state: { plan }) => state.plan?.plan);

  const limit = taskLimitByPlan[plan.subscription_type];

  useEffect(() => {
    setIsActive(task.status === TaskStatus.ENABLED);
  }, [task]);

  const queryClient = useQueryClient();

  const debouncedUpdate = useCallback(
    debounce(async (status: TaskStatus) => {
      if (task.status === status) return;
      // show error if task limit is reached
      if (status === TaskStatus.ENABLED && countOfActiveTasks >= limit) {
        notifyTaskScheduleError(dispatch, limit, "User has reached the maximum number of active task schedules for free plan", plan.subscription_type);
        setIsActive(false);
        return;
      };
      // handle tasks that cadence is 'once' and have already executed
      if (status === TaskStatus.ENABLED && task.cadence === Frequency.ONCE) {
        const taskDate = dayjs()
          .year(task.by_year)
          .month(task.by_month - 1)
          .date(task.by_month_day)
          .hour(task.by_hour)
          .minute(task.by_minute);

        if (taskDate.isBefore(dayjs())) {
          setIsActive(false);
          dispatch(setShowTaskModal(true));
          dispatch(setSelectedTask(task));
          dispatch(showNotification ({
            title: "This task cannot be scheduled",
            message: "Task has been executed. Please update the task schedule.",
            severity: "error",
            horizontal: "right",
          }));
          return;
        }
      }
      // update task
      try {
        await dispatch(updateTask({
          task: {
            ...task,
            status,
          } as Partial<TaskType>,
        })).unwrap();
        // optimistic UI update for task status in sidebar
        updateTaskStatus(queryClient, task.chat_session_id, task.id, status);
      }
      catch (err) {
        notifyTaskScheduleError(dispatch, limit, err.error, plan.subscription_type);
        setIsActive(false);
      }
    }, 1000),
    [task, countOfActiveTasks],
  );

  const updateStatus = () => {
    const newStatus = isActive ? TaskStatus.DISABLED : TaskStatus.ENABLED;
    setIsActive(!isActive);
    debouncedUpdate(newStatus);
  };

  const navigateToTaskSession = (e) => {
    e.stopPropagation();
    navigate(`/chat/${task.chat_session_id}`);
    dispatch(setShowTaskModal(false));
  };

  return (
    <div
      className={classNames(
        "p-6 bg-tgpt-secondary-accentLight rounded-lg",
        "hover:border border-elevation-outlined bg-tgpt-secondary-states-hover cursor-pointer",
      )}
    >
      <div className="flex flex-col gap-y-4 h-[262px]">
        <div className="flex justify-between">
          <div onClick={e => e.stopPropagation()}>
            <SwitchToggle
              checked={isActive}
              setChecked={updateStatus}
            />
            <Typography variant="overline/regular-400"className={`${isActive ? "text-success-main" : "dark:text-mui-white-38"}`}>
              {isActive ? "ACTIVE" : "INACTIVE"}
            </Typography>
          </div>
          <Tooltip title="Open conversation" placement="top" arrow>
            <IconButton onClick={e => navigateToTaskSession(e)} disabled={!task.chat_session_id}>
              <ChatRounded className="!fill-action-active"></ChatRounded>
            </IconButton>
          </Tooltip>
        </div>
        <div className="flex flex-col flex-grow overflow-hidden">
          <Typography variant="body1/regular-600" className="text-mui-black-87 dark:text-white pb-2 truncate">{task.name}</Typography>
          <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70 line-clamp-4 text-ellipsis">{task.user_query}</Typography>
        </div>
        <div className="flex flex-col gap-y-2">
          <div className="flex items-center gap-x-2">
            <UpdateRounded className="!fill-tgpt-secondary-light"></UpdateRounded>
            <Typography variant="caption/regular-400" className="text-tgpt-secondary-light text-left">{formattedString}</Typography>
          </div>
          <div className="flex items-center gap-x-2">
            {isActive ? <AlarmOnRounded className="!fill-tgpt-secondary-light"></AlarmOnRounded> : <AlarmOffRounded className="!fill-tgpt-secondary-light"></AlarmOffRounded>}
            {isActive
              ? (
                  <Typography variant="caption/regular-400" className="text-tgpt-secondary-light text-left">
                    Active since
                    {" "}
                    {formatDate(task.updated_at ?? task.created_at)}
                  </Typography>
                )
              : (
                  <Typography variant="caption/regular-400" className="text-tgpt-secondary-light text-left">
                    Paused since
                    {" "}
                    {formatDate(task.disabled_at)}
                  </Typography>
                )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Task;
